import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { GraduationCap, Check, AlertCircle, Eye, EyeOff, RefreshCw, ExternalLink } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StudentCredsEditor = ({ token, config, onSaved }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null); // {ok, msg}
  const [info, setInfo] = useState({ username: '', password_set: false });

  const fetchInfo = React.useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/student/credentials`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setInfo(res.data || {});
      setUsername(res.data?.username || '');
    } catch (e) { /* silent */ }
  }, [token]);

  useEffect(() => {
    if (token) fetchInfo();
  }, [token, fetchInfo]);

  const handleSave = async (e) => {
    e?.preventDefault();
    if (!username.trim()) {
      setResult({ ok: false, msg: 'İstifadəçi adı boş ola bilməz' });
      return;
    }
    setSaving(true);
    setResult(null);
    try {
      const body = { username: username.trim() };
      if (password.trim()) body.password = password.trim();
      await axios.put(`${API}/admin/student/credentials`, body,
        { headers: { Authorization: `Bearer ${token}` } });
      setResult({ ok: true, msg: 'Saxlanıldı! Tələbələr indi bu məlumatlarla daxil ola bilər.' });
      setPassword('');
      fetchInfo();
      onSaved && onSaved();
    } catch (e) {
      setResult({ ok: false, msg: e.response?.data?.detail || 'Xəta baş verdi' });
    } finally {
      setSaving(false);
    }
  };

  const studentUrl = `${window.location.origin}/student`;

  return (
    <div data-testid="student-creds-editor">
      <h3 className="admin-section-title flex items-center gap-2">
        <GraduationCap size={16} /> Tələbə girişi
      </h3>
      <p className="admin-section-desc">
        Tələbə portalı (<code>/student</code> səhifəsi) üçün paylaşılan istifadəçi adı və parol.
        Bütün tələbələr eyni hesabla daxil olur. Session 1 saat davam edir.
      </p>

      {/* Status banner */}
      <div
        style={{
          background: info.password_set ? 'rgba(34,197,94,0.06)' : 'rgba(234,179,8,0.06)',
          border: `1px solid ${info.password_set ? 'rgba(34,197,94,0.25)' : 'rgba(234,179,8,0.25)'}`,
          padding: '10px 14px',
          borderRadius: 10,
          marginBottom: 16,
          fontSize: 13,
        }}
      >
        {info.password_set ? (
          <span style={{ color: '#86efac' }}>
            ✓ Tələbə girişi aktivdir. Hazırkı istifadəçi adı: <strong>{info.username || '—'}</strong>
          </span>
        ) : (
          <span style={{ color: '#fde68a' }}>
            ⚠ Tələbə girişi hələ qurulmayıb — tələbələr daxil ola bilməz.
          </span>
        )}
      </div>

      <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div>
          <label className="admin-label">İstifadəçi adı</label>
          <input
            type="text"
            className="karma-input"
            style={{ width: '100%', textAlign: 'left' }}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            maxLength={64}
            placeholder="məs. tələbə"
            data-testid="student-username-input"
          />
        </div>

        <div>
          <label className="admin-label">
            {info.password_set ? 'Yeni parol (dəyişmək üçün)' : 'Parol'}
          </label>
          <div style={{ position: 'relative' }}>
            <input
              type={showPw ? 'text' : 'password'}
              className="karma-input"
              style={{ width: '100%', textAlign: 'left', paddingRight: 40 }}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              maxLength={128}
              placeholder={info.password_set ? '••• boş saxlasanız dəyişilməz' : 'Ən azı 4 simvol'}
              data-testid="student-password-input"
            />
            <button
              type="button"
              onClick={() => setShowPw(!showPw)}
              style={{
                position: 'absolute',
                right: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'transparent',
                border: 'none',
                color: 'rgba(255,255,255,0.5)',
                cursor: 'pointer',
                padding: 4,
              }}
              title={showPw ? 'Gizlə' : 'Göstər'}
            >
              {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
          </div>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginTop: 4 }}>
            Tez-tez dəyişdirmək tövsiyə olunur. Yalnız parolu dəyişmək üçün adı eyni saxlayın.
          </div>
        </div>

        <button
          type="submit"
          disabled={saving || !username.trim()}
          className="admin-btn-primary"
          data-testid="student-save-btn"
          style={{ alignSelf: 'flex-start' }}
        >
          {saving ? 'Saxlanır...' : 'Saxla'}
        </button>

        {result && (
          <div
            style={{
              padding: '10px 14px',
              background: result.ok ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)',
              border: `1px solid ${result.ok ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)'}`,
              borderRadius: 8,
              color: result.ok ? '#86efac' : '#fca5a5',
              fontSize: 13,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
            data-testid="student-save-result"
          >
            {result.ok ? <Check size={14} /> : <AlertCircle size={14} />}
            {result.msg}
          </div>
        )}
      </form>

      {info.password_set && (
        <div
          style={{
            marginTop: 22,
            padding: 14,
            background: 'rgba(168,85,247,0.05)',
            border: '1px solid rgba(168,85,247,0.2)',
            borderRadius: 10,
            fontSize: 13,
          }}
        >
          <div style={{ color: '#f0e68c', fontWeight: 600, marginBottom: 6 }}>
            Tələbə girişi linki
          </div>
          <a
            href={studentUrl}
            target="_blank"
            rel="noreferrer"
            style={{
              color: '#c4b5fd',
              textDecoration: 'underline',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              wordBreak: 'break-all',
            }}
            data-testid="student-portal-link"
          >
            {studentUrl} <ExternalLink size={12} />
          </a>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginTop: 8, lineHeight: 1.6 }}>
            Bu link-i tələbələrinizə göndərin. Hər biri yuxarıdakı istifadəçi adı və parolla daxil olub
            karma xəritəsindən istifadə edə bilər.
          </div>
        </div>
      )}

      <div
        style={{
          marginTop: 22,
          padding: '12px 14px',
          background: 'rgba(255,255,255,0.02)',
          border: '1px solid rgba(255,255,255,0.06)',
          borderRadius: 10,
          fontSize: 12,
          color: 'rgba(255,255,255,0.55)',
          lineHeight: 1.7,
        }}
      >
        <strong style={{ color: 'rgba(255,255,255,0.75)' }}>İpucu:</strong>
        <br />• Tələbə səhifəsinin xəritə görünüşünü dəyişmək üçün yuxarıdakı{' '}
        <strong>&ldquo;Tələbə səhifəsi&rdquo;</strong> seçimini aktivləşdirin və müvafiq tab-ları redaktə edin
        <br />• Bütün xəritə ayarları (başlıq, gates, custom nodes, AI, dil və s.) ana səhifədən
        tamamilə müstəqildir
      </div>
    </div>
  );
};

export default StudentCredsEditor;
